import React, { useCallback, useEffect, useRef, useState } from "react";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { fetchItem } from "../api/library.ts";
import { queryCursorSurface } from "../modeling/cursorSurfaceRaycast.ts";
import { AdjustLastOperationPanel } from "../ui/AdjustLastOperationPanel.tsx";
import { BevelDebugPanel } from "../ui/BevelDebugPanel.tsx";
import {
  ApplyGeneratorDialog,
  ConfirmDialog,
  SaveOrDiscardDialog,
} from "../ui/ConfirmDialog.tsx";
import { DebugConsolePanel } from "../ui/DebugConsolePanel.tsx";
import { DebugMaterialPanel } from "../ui/DebugMaterialPanel.tsx";
import { DebugMemoryPanel } from "../ui/DebugMemoryPanel.tsx";
import { DebugOverlay } from "../ui/DebugOverlay.tsx";
import { DebugRecorderPanel } from "../ui/DebugRecorderPanel.tsx";
import { DeleteMenu } from "../ui/DeleteMenu.tsx";
import { ExportMeshModal } from "../ui/ExportMeshModal.tsx";
import { ExtrudePieMenu } from "../ui/ExtrudePieMenu.tsx";
import { FavoritesPanel } from "../ui/FavoritesPanel.tsx";
import { FloatingPanelOverlays } from "../ui/FloatingPanels.tsx";
import { GravityMenu } from "../ui/GravityMenu.tsx";
import { HandPosePanel } from "../ui/HandPosePanel.tsx";
import { HeaderBar } from "../ui/HeaderBar.tsx";
import { InvalidSceneObjectsDialog } from "../ui/InvalidSceneObjectsDialog.tsx";
import { LoadingOverlay } from "../ui/LoadingOverlay.tsx";
import { MergeMenu } from "../ui/MergeMenu.tsx";
import { MeshMenu } from "../ui/MeshContextMenus.tsx";
import { ObjectDeleteConfirm } from "../ui/ObjectDeleteConfirm.tsx";
import { OperatorSearchPanel } from "../ui/OperatorsPanel.tsx";
import { PaletteTrack } from "../ui/PaletteTrack.tsx";
import { RelaxMenu } from "../ui/RelaxMenu.tsx";
import { RenderMethodModal } from "../ui/RenderMethodModal.tsx";
import { RenderMethodPieMenu } from "../ui/RenderMethodPieMenu.tsx";
import { SettingsModal } from "../ui/SettingsModal.tsx";
import { Sidebar, ToolbarStrip } from "../ui/Sidebar.tsx";
import { SmoothMenu } from "../ui/SmoothMenu.tsx";
import { SnapPieMenu } from "../ui/SnapPieMenu.tsx";
import { BottomRightBadges, CommandOverlay } from "../ui/StatusBar.tsx";
import { StorageQuotaDialog } from "../ui/StorageQuotaDialog.tsx";
import { ToastContainer } from "../ui/Toast.tsx";
import { showToast } from "../ui/toastBus.ts";
import { ToolPalette } from "../ui/ToolPalette.tsx";
import { TopologyVizPanel } from "../ui/TopologyVizPanel.tsx";
import { TrialStartModal } from "../ui/TrialStartModal.tsx";
import {
  COLOR_VIEWPORT_BG,
  DOCK_COLLAPSED_WIDTH,
  Z_BACKDROP,
} from "../ui/tokens.ts";
import { useAutoSurface } from "../ui/useAutoSurface.ts";
import { ViewPieMenu } from "../ui/ViewPieMenu.tsx";
import {
  type ContextMenuState,
  ViewportContextMenu,
} from "../ui/ViewportContextMenu.tsx";
import { BoneDebugPanel } from "../viewport/BoneDebugPanel.tsx";
import { CameraFrameOverlay } from "../viewport/CameraFrameOverlay.tsx";
import { CorrectiveShapeKeysDebugPanel } from "../viewport/CorrectiveShapeKeysDebugPanel.tsx";
import { InkingOverlay } from "../viewport/inking/InkingOverlay.tsx";
import { JointAngleDebugPanel } from "../viewport/JointAngleDebugOverlay.tsx";
import { PoseCaptureDebugPanel } from "../viewport/PoseCaptureDebugPanel.tsx";
import { ReferenceImageOverlay } from "../viewport/ReferenceImageOverlay.tsx";
import { ShadingDebugPanel } from "../viewport/ShadingDebugPanel.tsx";
import { ShapeKeyLayerDebugPanel } from "../viewport/ShapeKeyLayerDebugPanel.tsx";
import { Viewport3D } from "../viewport/Viewport3D.tsx";
import { ViewportBackground } from "../viewport/ViewportBackground.tsx";
import { ViewportCameraControls } from "../viewport/ViewportCameraControls.tsx";
import { ViewportModeTransition } from "../viewport/ViewportModeTransition.tsx";
import { resolveViewportBgPreset } from "../viewport/viewportBgPresets.ts";
import { getActivePage, setActivePage, useActivePage } from "./activePage.ts";
import { useAuthStore, useIsTrial } from "./authStore.ts";
import { FileDropZone } from "./FileDropZone.tsx";
import { useLibraryStore } from "./libraryStore.ts";
import {
  setMemoryLimit as setMemoryGovernorLimit,
  setSettingsOpener,
  startMemoryGovernor,
} from "./memoryGovernor.ts";
import { registerDefaultPressureHandlers } from "./memoryPressureHandlers.ts";
import { openSceneFromLibrary } from "./openFromLibrary.ts";
import { setOnSaveError } from "./persistence.ts";
import { useActiveRenderConfig } from "./renderConfig.ts";
import { getCurrentLibraryItemId } from "./saveToLibrary.ts";
import { useStore } from "./store.ts";
import { useKeyboardShortcuts } from "./useKeyboardShortcuts.ts";

/** Space the bottom ToolbarStrip reserves, plus a gap above it. */
const PALETTE_TRACK_BOTTOM_RESERVED_PX = DOCK_COLLAPSED_WIDTH + 12;

function SceneView() {
  useAutoSurface();
  const cameraKeysRef = useKeyboardShortcuts();
  // Owned here so both Viewport3D (producer) and ViewportCameraControls
  // (consumer) can share the same handles without Viewport3D rendering the
  // palette as a DOM sibling of <Canvas>.
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const cameraSyncRef = useRef<() => void>(() => {});
  const blendFiles = useStore((s) => s.blendFiles);
  const sceneObjectCount = useStore((s) => s.sceneObjects.size);
  const isEmpty = blendFiles.size === 0 && sceneObjectCount === 0;
  const isTrial = useIsTrial();
  const debugEnabled = useStore((s) => s.debugEnabled);
  const chromeHidden = useStore((s) => s.chromeHidden);
  const inkingEditing = useStore(
    (s) => s.workspaceMode === "inking" && s.inkingMode?.editSubMode === "edit",
  );
  const viewportBgPreset = useActiveRenderConfig((c) => c.viewportBgPreset);
  const bgPreset = resolveViewportBgPreset(viewportBgPreset);

  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const addObjectOpen = useStore((s) => s.addObjectOpen);
  const setAddObjectOpen = useStore((s) => s.setAddObjectOpen);
  const [debugMenuOpen, setDebugMenuOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const rightDownPos = useRef<{ x: number; y: number } | null>(null);

  // Suppress the native context menu everywhere in the viewport.
  // The custom menu is opened on pointerup instead, so drags are excluded.
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // Track right-button down/up globally. Only open the context menu if the
  // pointer didn't move (i.e. it was a tap, not a camera-pan drag).
  useEffect(() => {
    const TAP_SQ = 36; // 6px threshold squared
    const onDown = (e: PointerEvent) => {
      if (e.button === 2) rightDownPos.current = { x: e.clientX, y: e.clientY };
    };
    const onUp = (e: PointerEvent) => {
      if (e.button !== 2) return;
      const down = rightDownPos.current;
      rightDownPos.current = null;
      if (!down) return;
      const dx = e.clientX - down.x;
      const dy = e.clientY - down.y;
      if (dx * dx + dy * dy > TAP_SQ) return;
      // Don't show during G/R/S command input
      if (useStore.getState().commandBuffer) return;
      // Don't override context menu on UI elements
      const target = e.target as HTMLElement;
      if (target.closest("button, input, select, textarea, [data-no-ctx]"))
        return;

      // Shift+Right-click: place 3D cursor on the surface under the mouse.
      if (e.shiftKey) {
        const rect = window.__poseref_canvasRect?.();
        if (!rect) return;
        const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
        const hit = queryCursorSurface(ndcX, ndcY);
        if (hit) {
          const s = useStore.getState();
          s.pushUndo();
          s.setCursor3DPosition(hit);
        }
        return;
      }

      const s = useStore.getState();
      const em = s.editMode;
      if (em && !em.isCurve) {
        // In mesh edit mode: open the context-sensitive mesh menu
        s.setOperatorAnchorPoint({ x: e.clientX, y: e.clientY });
        s.setMeshMenuSection(em.granularity);
        s.setMeshMenuOpen(true);
      } else {
        setContextMenu({ x: e.clientX, y: e.clientY });
      }
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const closeAll = useCallback(() => {
    // Note: favoritesOpen (FavoritesPanel) is intentionally excluded — it only
    // closes via its close button or toolbar toggle.
    setProjectOpen(false);
    setAddObjectOpen(false);
    setDebugMenuOpen(false);
    setContextMenu(null);
  }, [setAddObjectOpen]);

  // Listen for toggle-favorites events from sidebar controls
  useEffect(() => {
    const onFavorites = () => {
      setFavoritesOpen((prev) => !prev);
    };
    window.addEventListener("toggle-favorites", onFavorites);
    return () => {
      window.removeEventListener("toggle-favorites", onFavorites);
    };
  }, []);

  return (
    <div
      onContextMenu={handleContextMenu}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        background: COLOR_VIEWPORT_BG,
      }}
    >
      {/* Viewport area — flex:1, contains the 3D canvas and all overlays */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          position: "relative",
          overflow: "hidden",
          background: COLOR_VIEWPORT_BG,
        }}
      >
        <ViewportBackground preset={bgPreset} />
        <Viewport3D
          cameraKeysRef={cameraKeysRef}
          controlsRef={controlsRef}
          cameraSyncRef={cameraSyncRef}
        />

        {/* Masks the render-scope transition (viewport ↔ render/inking) so the
          geometry-subdivision + pipeline-rebuild stall isn't visible as a
          flash. Sits above the Canvas, below floating panels. */}
        <ViewportModeTransition />

        {/* Inking stroke overlay — 2D canvas above viewport */}
        <InkingOverlay />

        {/* Camera frame overlay */}
        <CameraFrameOverlay />

        {/* Reference image overlay */}
        <ReferenceImageOverlay />

        {/* Command display — bottom-right near axis gizmo */}
        <CommandOverlay />

        {/* Scene name — bottom-right, tap to rename */}
        {!chromeHidden && <BottomRightBadges />}

        {/* Trial users see TrialStartModal on empty scene */}
        {isTrial && <TrialStartModal isEmpty={isEmpty} />}

        {/* Header bar */}
        {!chromeHidden && (
          <HeaderBar
            projectOpen={projectOpen}
            onToggleProject={() => {
              const next = !projectOpen;
              closeAll();
              setProjectOpen(next);
            }}
            debugEnabled={debugEnabled}
            debugMenuOpen={debugMenuOpen}
            onToggleDebugMenu={() => {
              const next = !debugMenuOpen;
              closeAll();
              setDebugMenuOpen(next);
            }}
          />
        )}

        {/* Backdrop to close panels on outside tap */}
        {(projectOpen || addObjectOpen) && (
          <div
            onPointerDown={closeAll}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: Z_BACKDROP,
            }}
          />
        )}

        {/* Panels */}
        {favoritesOpen && (
          <FavoritesPanel onClose={() => setFavoritesOpen(false)} />
        )}

        {/* Floating panels (from header toggles) */}
        <FloatingPanelOverlays />

        {/* After-op / redo panel (F9) */}
        <AdjustLastOperationPanel />

        {/* Operator search panel (F3) */}
        <OperatorSearchPanel />

        {/* Delete options menu (X/Delete in edit mode) */}
        <DeleteMenu />

        {/* Render-method pie menu (Z) */}
        <RenderMethodPieMenu />

        {/* Cursor snap pie menu (Shift+S) */}
        <SnapPieMenu />

        {/* Axis-view pie menu (`) */}
        <ViewPieMenu />

        {/* Extrude variant pie menu (Alt+E) */}
        <ExtrudePieMenu />

        {/* Delete confirmation popover (X in object mode) */}
        <ObjectDeleteConfirm />

        {/* Merge options menu (M in edit mode) */}
        <MergeMenu />

        {/* Bone relax popover (X in pose mode) */}
        <RelaxMenu />

        {/* Simulate Gravity elasticity popover */}
        <GravityMenu />

        {/* Smooth Vertices popover (opened from the mesh context menu / Shift+O) */}
        <SmoothMenu />

        {/* Unified mesh context menu (Ctrl+V / Ctrl+E / Ctrl+F in edit mode) */}
        <MeshMenu />

        {/* Hand pose panel */}
        <HandPosePanel />

        {/* Left-edge palette track — hosts the tool strip and the camera
          controls. The track measures each palette's rendered height and
          keeps them from overlapping as the user drags or content reflows. */}
        {!chromeHidden && (
          <PaletteTrack bottomReservedPx={PALETTE_TRACK_BOTTOM_RESERVED_PX}>
            <ToolPalette />
            {!inkingEditing && (
              <ViewportCameraControls
                controlsRef={controlsRef}
                syncRef={cameraSyncRef}
              />
            )}
          </PaletteTrack>
        )}

        {/* Debug overlay */}
        <DebugConsolePanel />
        <DebugOverlay />
        <DebugMaterialPanel />
        <BevelDebugPanel />
        <BoneDebugPanel />
        <JointAngleDebugPanel />
        <ShapeKeyLayerDebugPanel />
        <CorrectiveShapeKeysDebugPanel />
        <PoseCaptureDebugPanel />
        <TopologyVizPanel />
        <ShadingDebugPanel />
        <DebugRecorderPanel />
        <DebugMemoryPanel />

        {/* Settings modal */}
        <SettingsModal />
        <RenderMethodModal />

        {/* Export mesh modal */}
        <ExportMeshModal />

        {/* Bottom toolbar — horizontal arm of the L-shape */}
        {!chromeHidden && <ToolbarStrip />}

        {/* Viewport context menu */}
        <ViewportContextMenu menu={contextMenu} onClose={closeContextMenu} />
      </div>

      {/* Right-side panel dock */}
      {!chromeHidden && <Sidebar />}
    </div>
  );
}

// Lazy-load pages to keep the initial bundle fast
const LoginPage = React.lazy(() =>
  import("../ui/auth/LoginPage.tsx").then((m) => ({ default: m.LoginPage })),
);
const LibraryPage = React.lazy(() =>
  import("../ui/library/LibraryPage.tsx").then((m) => ({
    default: m.LibraryPage,
  })),
);
const AccountSettingsPage = React.lazy(() =>
  import("../ui/account/AccountSettingsPage.tsx").then((m) => ({
    default: m.AccountSettingsPage,
  })),
);

export function App() {
  const page = useActivePage();

  // Apply persisted UI scale on mount
  useEffect(() => {
    const scale = useStore.getState().uiScale;
    if (scale !== 1) document.documentElement.style.zoom = String(scale);
    return () => {
      document.documentElement.style.zoom = "";
    };
  }, []);

  // Boot the memory governor: install pressure-relief handlers, wire the
  // settings deep-link so memory toasts can offer a one-click CTA into the
  // Performance tab, and seed the governor with the persisted limit. The
  // governor itself does not import the store; it reaches the UI exclusively
  // through these registered callbacks.
  useEffect(() => {
    const disposeHandlers = registerDefaultPressureHandlers();
    const disposeGovernor = startMemoryGovernor();
    setSettingsOpener(() => useStore.getState().openSettings("system"));
    setMemoryGovernorLimit(useStore.getState().memoryLimitBytes);
    return () => {
      setSettingsOpener(null);
      disposeGovernor();
      disposeHandlers();
    };
  }, []);

  // Wire save-error callback to surface IndexedDB storage limit issues via
  // toast.  Throttle to avoid spamming the user — the debounced auto-save
  // fires every 1 s, so without throttling the toast would repeat endlessly
  // once storage is full.
  useEffect(() => {
    let lastToastAt = 0;
    const THROTTLE_MS = 30_000; // show at most once every 30 s
    setOnSaveError((e) => {
      const now = Date.now();
      if (now - lastToastAt < THROTTLE_MS) return;
      lastToastAt = now;
      const isStorageLimit =
        (e instanceof Error &&
          (e.name === "StorageFullError" || e.name === "SceneTooLargeError")) ||
        (e instanceof DOMException &&
          (e.name === "QuotaExceededError" || e.code === 22));
      showToast(
        isStorageLimit
          ? ((e as Error).message ??
              "Browser storage is full — local backup could not be saved.")
          : "Failed to save local backup.",
        { severity: "error", duration: 6000 },
      );
    });
    return () => setOnSaveError(null);
  }, []);

  // Surface store-level errors as toasts.  Every save/load failure already
  // sets `useStore.setState({ error })` — this subscription turns that into
  // a visible notification without patching individual call sites.
  useEffect(() => {
    const unsubApp = useStore.subscribe((state, prev) => {
      if (state.error && state.error !== prev.error) {
        showToast(state.error, { severity: "error", duration: 5000 });
      }
    });
    const unsubLib = useLibraryStore.subscribe((state, prev) => {
      if (state.error && state.error !== prev.error) {
        showToast(state.error, { severity: "error", duration: 5000 });
      }
    });
    return () => {
      unsubApp();
      unsubLib();
    };
  }, []);

  // Check session on mount — only redirect if currently on login page.
  // If we're already on library or viewport (restored from sessionStorage),
  // just validate the token silently without navigating.
  useEffect(() => {
    useAuthStore
      .getState()
      .checkSession()
      .then(() => {
        const user = useAuthStore.getState().user;
        if (!user) {
          setActivePage("login");
        } else {
          const tier = useAuthStore.getState().storageUsage?.tier;
          // Only redirect from the login page — if restoring from sessionStorage, leave them where they were
          if (getActivePage() === "login") {
            setActivePage(tier === "unsubscribed" ? "viewport" : "library");
          }
        }
      });
  }, []);

  // On a fresh page load, sessionStorage may say "viewport" but the store
  // is empty (it doesn't persist scene data). If we have a stored library
  // item ID, reload that scene automatically so the user lands back in the
  // editor. Otherwise fall back to the library.
  useEffect(() => {
    if (getActivePage() === "viewport") {
      const s = useStore.getState();
      if (s.blendFiles.size === 0 && s.sceneObjects.size === 0) {
        const itemId = getCurrentLibraryItemId();
        if (itemId) {
          // Re-open the scene that was active before refresh.
          // The canvas is already mounted (page was "viewport"), so
          // onCreated already fired. We need to clear loading manually
          // since openSceneFromLibrary leaves it true for the canvas
          // transition that won't happen here.
          fetchItem(itemId)
            .then((item) => openSceneFromLibrary(item))
            .then(() => useStore.setState({ loading: false }))
            .catch(() => setActivePage("library"));
        } else {
          // Trial users have no library — keep them at the viewport
          // (TrialStartModal will surface for the empty scene).
          const tier = useAuthStore.getState().storageUsage?.tier;
          if (tier !== "unsubscribed") setActivePage("library");
        }
      }
    }
  }, []);

  // Subscribe to auth changes — redirect to login on logout, route new logins appropriately
  useEffect(() => {
    const unsub = useAuthStore.subscribe((state, prev) => {
      if (prev.user && !state.user) setActivePage("login");
      if (!prev.user && state.user && getActivePage() === "login") {
        const tier = state.storageUsage?.tier;
        // Trial (unsubscribed) users land in the viewport editor, not the library
        setActivePage(tier === "unsubscribed" ? "viewport" : "library");
      }
    });
    return unsub;
  }, []);

  return (
    <div
      onContextMenu={(e) => e.preventDefault()}
      style={{ display: "contents" }}
    >
      <LoadingOverlay />
      <React.Suspense
        fallback={
          <div style={{ position: "fixed", inset: 0, background: "#0e0e10" }} />
        }
      >
        {page === "login" && <LoginPage />}
        {page === "library" && <LibraryPage />}
        {page === "accountSettings" && <AccountSettingsPage />}
        {page === "viewport" && (
          <FileDropZone>
            <SceneView />
          </FileDropZone>
        )}
      </React.Suspense>
      <ToastContainer />
      <InvalidSceneObjectsDialog />
      <ConfirmDialog />
      <SaveOrDiscardDialog />
      <ApplyGeneratorDialog />
      <StorageQuotaDialog />
    </div>
  );
}
